<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBaseView, useDateTime, useOrder, useOrderPayments } from "@/composables";
import { useAppStore } from "@/store/app.store";
import { useSnackbarStore } from "@/store/snackbar.store";
import { OrderFreightType, OrderStopStatus, OrderStopTimeType, OrderStopType } from "@/constants/order";
import OrderValidator from "@/validations/order.validator";
import { generateId } from "@/utils/generateId";
import OmniForm from "@/components/omni/OmniForm.vue";
import FormActions from "@/components/form/FormActions.vue";
import OrderEditInfo from "@/views/orders/edit/components/OrderEditInfo.vue";
import OrderEditFiles from "@/views/orders/edit/components/OrderEditFiles.vue";
import OrderEditStops from "@/views/orders/edit/components/OrderEditStops.vue";
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import OrderStatus from "@/components/statuses/OrderStatus.vue";
import InfoMessage from "@/components/messages/InfoMessage.vue";
import type { Order, OrderDto, OrderFreight, OrderStop } from "@/api/orders/dto/order.dto";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { setTitle, scrollToError } = useBaseView();
const { dateTZ } = useDateTime();
const { loadOrder, upsertOrder } = useOrder();
const { createMandatoryOrderPayment } = useOrderPayments();

const appStore = useAppStore();
const snackbarStore = useSnackbarStore();

const form = ref<InstanceType<typeof OmniForm> | null>(null);
const filesForm = ref<InstanceType<typeof OrderEditFiles> | null>(null);
const originalOrder = ref<OrderDto | null>(null);
const loading = ref(false);
const isSubmitting = ref(false);
const order = ref<Order>({
  freights: [],
  orderStops: [],
  orderPayments: createMandatoryOrderPayment([]),
  refNumbers: [],
  sendUpdatesTo: [],
  rate: 0,
  underTracking: false,
  teamForBroker: false,
  protectFromFreezing: false,
  requiredDriverCertificates: [],
  hazmat: false,
  temperatures: [],
  $hasTemperatureControl: false,
});

const createdAt = computed(() => {
  return appStore.dispatcher && order.value.createdAt
    ? dateTZ(order.value.createdAt, appStore.dispatcher.timezone)
    : "";
});

const isCreateNew = computed(() => route.params.id === undefined);

async function load() {
  if (isCreateNew.value) {
    setTitle("New Order", "Orders");

    addStop(OrderStopType.pickup);
    addStop(OrderStopType.delivery);

    updateDeliveryFreights();

    return;
  }

  loading.value = true;
  const loadedOrder = await loadOrder(String(route.params.id));

  if (loadedOrder) {
    originalOrder.value = JSON.parse(JSON.stringify(loadedOrder));
    order.value = loadedOrder;

    order.value.orderPayments = createMandatoryOrderPayment(order.value.orderPayments ?? [], order.value.status);

    if (order.value.temperatures?.length === 2 || order.value.protectFromFreezing) {
      order.value.$hasTemperatureControl = true;
    }
  } else {
    snackbarStore.showSnackbarError("Error loading order data");
  }

  loading.value = false;
}

function addFreight(stop: OrderStop) {
  const id = generateId();
  const freight: OrderFreight = {
    id,
    type: OrderFreightType.pallets,
    quantity: 1,
    weight: null,
    stackable: true,
    isDeleted: false,
  };

  order.value.freights.push(freight);
  stop.freights?.push(id);
}

function addStop(stopType: OrderStopType) {
  const stop: OrderStop = {
    tempId: generateId(),
    type: stopType,
    status: OrderStopStatus.notStarted,
    freights: [],
    note: "",
    timeType: OrderStopTimeType.fcfs,
    timeFrom: null,
    timeTo: null,
    time2Type: OrderStopTimeType.fcfs,
    time2From: null,
    time2To: null,
    isDeleted: false,
    position: order.value.orderStops.length + 1,
  };

  if (stopType === OrderStopType.pickup) {
    addFreight(stop);
  }

  order.value.orderStops.push(stop);
}

async function save() {
  const validation = await form.value?.validate();
  const isOrderValid = new OrderValidator(order.value).validate();

  if (!validation?.valid || !isOrderValid) {
    scrollToError();
    return;
  }

  isSubmitting.value = true;
  const { polyline, ...orderData } = order.value;
  const savedOrder = await upsertOrder({
    ...orderData,
    orderStops: orderData.orderStops.map(({ tempId, ...rest }) => rest),
    orderPayments: orderData.orderPayments.map(({ tempId, ...rest }) => rest),
  });

  if (savedOrder?.id) {
    await filesForm.value?.saveFiles(savedOrder.id);
    snackbarStore.showSnackbarSuccess(t("general.saved"));

    if (order.value.tripId) {
      router.push({ name: "trip-edit", params: { id: order.value.tripId } });
    } else {
      router.push({ name: "order-view", params: { id: savedOrder.id } });
    }
  } else {
    snackbarStore.showSnackbarError(t("general.save-error"));
  }

  isSubmitting.value = false;
}

function goBack() {
  router.back();
}

function updateDeliveryFreights() {
  const allOrderFreightIds = order.value.freights.flatMap((freight) => freight.id ?? []);
  const hasOnlyOneUnstartedDelivery =
    order.value.orderStops.filter(
      (orderStop) => orderStop.type === OrderStopType.delivery && orderStop.status === OrderStopStatus.notStarted,
    ).length === 1;

  order.value = {
    ...order.value,
    orderStops: order.value.orderStops.map((orderStop) => {
      const isDelivery = orderStop.type === OrderStopType.delivery;
      const isDeleted = orderStop.isDeleted;
      const hasProcessStarted = orderStop.status !== OrderStopStatus.notStarted;

      if (!isDelivery || isDeleted || hasProcessStarted) {
        return orderStop;
      }

      if (hasOnlyOneUnstartedDelivery) {
        return {
          ...orderStop,
          freights: allOrderFreightIds,
        };
      }

      return {
        ...orderStop,
        freights: (orderStop.freights ?? []).filter((freight) => allOrderFreightIds.includes(freight)),
      };
    }),
  };
}

onMounted(async () => {
  await load();
});

watch(
  () => appStore.dispatcher,
  (dispatcher) => {
    if (isCreateNew.value && dispatcher) {
      order.value.createdBy = dispatcher.id;
    }
  },
  { immediate: true },
);

watch(
  () => order.value.$hasTemperatureControl,
  (hasTemperatureControl) => {
    if (!hasTemperatureControl) {
      order.value.temperatures = [];
      order.value.protectFromFreezing = false;
    }
  },
);
</script>
<template>
  <omni-form ref="form">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
      <div v-if="order.id" class="d-flex align-center">
        <h3 class="title font-weight-medium">Order #{{ order.number }}</h3>
        <order-status v-if="order.status" :order="order" class="ml-4"></order-status>
        <info-message v-if="order.tripId" class="ml-6">
          After changing order information, you will be automatically redirected to the Trip manage page to synchronize
          information
        </info-message>
      </div>
      <h3 v-else class="title font-weight-medium">Create new order</h3>
      <div class="d-flex align-center flex-wrap">
        <div class="d-flex align-center mr-5">
          <span class="text-grey-darken-1 mr-2 mb-1">Created date:</span>
          <span class="text-grey-darken-3">{{ createdAt }}</span>
        </div>
        <dispatcher-select v-model="order.createdBy" :disabled="true" data-qa="created_by" label="Created by" />
      </div>
    </div>

    <order-edit-info v-model="order"></order-edit-info>
    <order-edit-stops
      v-model="order"
      :original-order="originalOrder"
      @update-delivery-freights="updateDeliveryFreights"
    ></order-edit-stops>
    <order-edit-files v-if="!loading" ref="filesForm" :order="order"></order-edit-files>
    <form-actions :loading="isSubmitting" :order="order" @cancel="goBack" @submit="save"></form-actions>
  </omni-form>
</template>

<style lang="scss">
.v-application--is-ltr .create-order .v-text-field .v-input__prepend-inner {
  padding-right: 8px;
}

.v-input.v-input--is-label-active {
  &.text-orange {
    .theme--light.v-label {
      color: #fb8c00;
    }
  }

  &.text-green {
    .theme--light.v-label {
      color: #4caf50;
    }
  }
}
</style>
